import { auth } from '../config/firebase';

const API_BASE = 'https://business-search-api-815700675676.us-central1.run.app';

export interface CreditBalance {
  available: number;
  used: number;
  purchased: number;
  monthlyAllocation: number;
  lastUpdated?: string;
}

export interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price: number;
  priceFormatted: string;
  pricePerCredit: string;
  badge: 'popular' | 'best_value' | null;
}

export interface PurchaseResult {
  success: boolean;
  checkoutUrl?: string;
  sessionId?: string;
  error?: string;
}

export type CreditPackId = string;

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      headers['Authorization'] = `Bearer ${token}`;
    } catch (err) {
      console.error('Failed to get ID token:', err);
    }
  }

  return headers;
}

class CreditsService {
  private static instance: CreditsService;
  private cachedBalance: CreditBalance | null = null;
  private cachedPacks: CreditPack[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 30000;

  private constructor() {}

  public static getInstance(): CreditsService {
    if (!CreditsService.instance) {
      CreditsService.instance = new CreditsService();
    }
    return CreditsService.instance;
  }

  async getBalance(forceRefresh = false): Promise<CreditBalance> {
    if (!forceRefresh && this.cachedBalance && Date.now() - this.cacheTimestamp < this.CACHE_DURATION) {
      return this.cachedBalance;
    }

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/user/credits`, { headers });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Not authenticated');
        }
        throw new Error('Failed to fetch credits');
      }

      const data = await response.json();
      this.cachedBalance = data;
      this.cacheTimestamp = Date.now();
      return data;
    } catch (error) {
      console.error('Failed to fetch balance:', error);
      return {
        available: 0,
        used: 0,
        purchased: 0,
        monthlyAllocation: 0
      };
    }
  }

  async getCreditPacks(): Promise<CreditPack[]> {
    if (this.cachedPacks) {
      return this.cachedPacks;
    }

    try {
      const response = await fetch(`${API_BASE}/api/stripe/credit-packs`);
      if (!response.ok) {
        throw new Error('Failed to fetch credit packs');
      }
      const data = await response.json();
      this.cachedPacks = data.packs;
      return data.packs;
    } catch (error) {
      console.error('Failed to fetch packs:', error);
      return [];
    }
  }

  async purchaseCredits(packId: string): Promise<PurchaseResult> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/stripe/create-credit-checkout`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          packId,
          successUrl: `${window.location.origin}/dashboard?tab=credits&success=true`,
          cancelUrl: `${window.location.origin}/dashboard?tab=credits&canceled=true`
        })
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.error || 'Failed to create checkout session'
        };
      }

      const data = await response.json();

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }

      return {
        success: true,
        checkoutUrl: data.checkoutUrl,
        sessionId: data.sessionId
      };
    } catch (error) {
      console.error('Failed to purchase credits:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to initiate purchase'
      };
    }
  }

  invalidateCache(): void {
    this.cachedBalance = null;
    this.cacheTimestamp = 0;
  }
}

export const creditsService = CreditsService.getInstance();
